import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Row,
  Col,
  Upload,
  UploadProps,
  GetProp,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';
import { EventType, EventUpdateRequest, UserType } from '../../../types/types';
import {
  EntityStatus,
  EntityType,
  EventStatus,
  UserRole,
} from '../../../enums/enums';
import { updateEvent } from '../../../api/requests/events';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';
import {
  CameraOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { UnifiedImage } from '../Facility/FacilityFormModal';
import { getBase64 } from './Steps/Step1BasicInfo';
import {
  bulkUploadFilesRequest,
  deleteAttachmentRequest,
} from '../../../api/requests/attachments';
import { getFileOrders } from '../../../pages/Facility';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import LocationPicker, { LocationValue } from '../Maps/LocationPicker';
import Modal from '../../common/Modal';
import {
  searchUsersRequest,
  getUserByIdRequest,
} from '../../../api/requests/users';
import { RcFile } from 'antd/es/upload';
import { debounce } from '../../../utils/utils';

const { Option } = Select;
const { TextArea } = Input;

interface EventEditFormProps {
  event: EventType | null;
  onSuccess: () => void;
  onCancel: () => void;
}

type FormState = {
  eventName: string;
  eventDescription: string;
  eventStartDate: Dayjs | null;
  eventEndDate: Dayjs | null;
  registrationStartDate: Dayjs | null;
  registrationEndDate: Dayjs | null;
  eventStatus: EventStatus | undefined;
  registrationFee: number;
  eventLocation?: string;
  eventLocationLatitude?: number | null;
  eventLocationLongitude?: number | null;
  eventOrganizerUserId?: number | null;
};

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

type FormErrors = Partial<Record<keyof FormState, string>>;

const eventStatusOptions = [
  { label: 'Draft', value: EventStatus.Draft },
  { label: 'Published', value: EventStatus.Open },
  { label: 'In Progress', value: EventStatus.InProgress },
  { label: 'Completed', value: EventStatus.Completed },
  { label: 'Cancelled', value: EventStatus.Cancelled },
];

const validateForm = (formState: FormState): FormErrors => {
  const errors: FormErrors = {};

  // Validate event name
  if (!formState.eventName?.trim()) {
    errors.eventName = 'Event name is required';
  }

  // Validate description
  if (!formState.eventDescription?.trim()) {
    errors.eventDescription = 'Description is required';
  }

  // Validate registration start date
  if (!formState.registrationStartDate) {
    errors.registrationStartDate = 'Registration start date is required';
  }

  // Validate registration end date
  if (!formState.registrationEndDate) {
    errors.registrationEndDate = 'Registration end date is required';
  }

  // Validate event start date
  if (!formState.eventStartDate) {
    errors.eventStartDate = 'Event start date is required';
  }

  // Validate event end date
  if (!formState.eventEndDate) {
    errors.eventEndDate = 'Event end date is required';
  }

  // Validate status
  if (formState.eventStatus === undefined || formState.eventStatus === null) {
    errors.eventStatus = 'Event status is required';
  }

  // Validate date ranges - only if dates exist
  if (formState.registrationStartDate && formState.registrationEndDate) {
    if (
      formState.registrationStartDate.isAfter(formState.registrationEndDate)
    ) {
      errors.registrationEndDate =
        'Registration end must be after registration start';
    }
  }

  // Validate registration end is before event start
  if (formState.registrationEndDate && formState.eventStartDate) {
    if (!formState.registrationEndDate.isBefore(formState.eventStartDate)) {
      errors.eventStartDate = 'Event start must be after registration ends';
    }
  }

  // Validate event date range
  if (formState.eventStartDate && formState.eventEndDate) {
    if (formState.eventStartDate.isAfter(formState.eventEndDate)) {
      errors.eventEndDate = 'Event end must be after event start';
    }
  }

  return errors;
};

const withStartOfDay = (date: Dayjs | null) =>
  date ? date.startOf('day') : null;

const withEndOfDay = (date: Dayjs | null) => (date ? date.endOf('day') : null);

const EventEditForm: React.FC<EventEditFormProps> = ({
  event,
  onSuccess,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<FormState>({
    eventName: '',
    eventDescription: '',
    eventStartDate: null,
    eventEndDate: null,
    registrationStartDate: null,
    registrationEndDate: null,
    eventLocation: '',
    eventStatus: undefined,
    registrationFee: 0,
    eventOrganizerUserId: null,
  });
  const [images, setImages] = useState<UnifiedImage[]>([]);
  const [locationPickerOpen, setLocationPickerOpen] = useState<boolean>(false);
  const snackbar = useSnackbar();
  const [eventOrganisers, setEventOrganisers] = useState<UserType[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [isLoadingOrganisers, setIsLoadingOrganisers] =
    useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMoreOrganisers, setHasMoreOrganisers] = useState<boolean>(true);

  // Fetch a specific organiser by ID (for initial selected value)
  const fetchOrganiserById = useCallback(
    async (userId: number) => {
      try {
        const response = await getUserByIdRequest(userId);
        if (response && response.isSuccess && response.value) {
          // Check if user is already in the list
          const user = response.value;
          const exists = eventOrganisers.some(
            (org) => org.userId === user?.userId,
          );
          if (!exists && user !== undefined) {
            // Add to the beginning of the list
            const userObj: UserType = {
              userRole: user.role,
              userId: user.userId,
              fullName: user.fullName,
              email: user.email,
              phoneNumber: user.phoneNumber,
              profilePictureUrl: user.profilePictureUrl,
              entityStatus: user.entityStatus,
              createdOn: user.createdOn,
              bio: user.bio,
            };
            setEventOrganisers((prev) => [
              userObj,
              ...prev.filter((org) => org.userId !== userObj.userId),
            ]);
          }
        }
      } catch (error) {
        console.error('Error fetching organiser by ID:', error);
      }
    },
    [eventOrganisers],
  );

  const fetchEventOrganisers = async (
    searchQuery: string,
    page: number,
    append: boolean,
  ) => {
    try {
      setIsLoadingOrganisers(true);
      const organisersResponse = await searchUsersRequest({
        userRole: UserRole.EventOrganizer,
        EntityStatus: EntityStatus.Active,
        SearchQuery: searchQuery || undefined,
        PageNumber: page,
        PageSize: 20, // Fetch 20 records per page
      });
      if (organisersResponse && organisersResponse.isSuccess) {
        const newOrganisers = organisersResponse.value?.rows || [];
        if (append) {
          setEventOrganisers((prev) => [...prev, ...newOrganisers]);
        } else {
          setEventOrganisers(newOrganisers);
        }
        // Check if there are more records
        const totalRecords = organisersResponse.value?.totalCount || 0;
        setHasMoreOrganisers(page * 20 < totalRecords);
      }
    } catch (error) {
      console.error('Error fetching event organisers:', error);
    } finally {
      setIsLoadingOrganisers(false);
    }
  };

  const fetchDefaultOrganisers = async (
    searchQuery: string,
    page: number,
    append: boolean,
  ) => {
    try {
      await fetchEventOrganisers(searchQuery, page, append);

      if (event?.eventOrganizerUserId)
        await fetchOrganiserById(event?.eventOrganizerUserId);
    } catch (error) {
      console.error('Error fetching default organisers:', error);
    }
  };

  // Debounced search function
  const debouncedSearchOrganisers = useMemo(
    () =>
      debounce((searchQuery: string) => {
        setCurrentPage(0);
        if (searchQuery.trim() === '') {
          fetchDefaultOrganisers('', 0, false);
        } else {
          fetchEventOrganisers(searchQuery, 0, false);
        }
      }, 500),
    [],
  );

  useEffect(() => {
    fetchDefaultOrganisers('', 0, false);
  }, []);

  // Handle search input change
  const handleOrganiserSearch = (value: string) => {
    setSearchText(value);
    debouncedSearchOrganisers(value);
  };

  // Handle scroll to load more
  const handleOrganiserScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    if (
      target.scrollTop + target.clientHeight >= target.scrollHeight - 10 &&
      !isLoadingOrganisers &&
      hasMoreOrganisers
    ) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchEventOrganisers(searchText, nextPage, true);
    }
  };

  const eventOrganiserOptions = useMemo(() => {
    return eventOrganisers.map((organiser) => ({
      label: organiser.fullName,
      value: organiser.userId,
    }));
  }, [eventOrganisers]);

  // Initialize form with event data
  useEffect(() => {
    if (event) {
      const initialFormState: FormState = {
        eventName: event.eventName || '',
        eventDescription: event.eventDescription || '',
        eventStartDate: event.eventStartDate
          ? dayjs(event.eventStartDate)
          : null,
        eventEndDate: event.eventEndDate ? dayjs(event.eventEndDate) : null,
        registrationStartDate: event.registrationStartDate
          ? dayjs(event.registrationStartDate)
          : null,
        registrationEndDate: event.registrationEndDate
          ? dayjs(event.registrationEndDate)
          : null,
        eventLocation: event.eventLocation || '',
        eventLocationLatitude: event.eventLocationLatitude,
        eventLocationLongitude: event.eventLocationLongitude,
        eventStatus: event.eventStatus,
        registrationFee: event.registrationFee || 0,
        eventOrganizerUserId: event.eventOrganizerUserId || null,
      };

      setFormState(initialFormState);
      form.setFieldsValue({
        eventName: initialFormState.eventName,
        eventDescription: initialFormState.eventDescription,
        eventStartDate: initialFormState.eventStartDate,
        eventEndDate: initialFormState.eventEndDate,
        registrationStartDate: initialFormState.registrationStartDate,
        registrationEndDate: initialFormState.registrationEndDate,
        eventLocation: initialFormState.eventLocation,
        eventStatus: initialFormState.eventStatus,
        registrationFee: initialFormState.registrationFee,
      });
      setImages(
        event.primaryImageUrl
          ? [
              {
                url: event.primaryImageUrl,
                file: undefined,
                isPrimary: true,
                isNew: false,
              },
            ]
          : [],
      );
    }
  }, [event, form, eventOrganisers, fetchOrganiserById]);

  const handleSubmit = async () => {
    try {
      if (!event?.eventId) {
        snackbar.error('Event data is missing. Cannot update event.');
        return;
      }
      // Validate form
      const validationErrors = validateForm(formState);
      if (Object.keys(validationErrors).length > 0) {
        Object.entries(validationErrors).forEach(([field, error]) => {
          form.setFields([{ name: field, errors: [error] }]);
        });
        return;
      }
      if (!images || images.length === 0) {
        snackbar.error('Please upload an event image.');
        return;
      }

      setLoading(true);

      const payload: EventUpdateRequest = {
        eventName: formState.eventName,
        eventDescription: formState.eventDescription,
        eventStartDate: withStartOfDay(formState.eventStartDate),
        eventEndDate: withEndOfDay(formState.eventEndDate),
        registrationStartDate: withStartOfDay(formState.registrationStartDate),
        registrationEndDate: withEndOfDay(formState.registrationEndDate),
        eventStatus: formState.eventStatus,
        registrationFee: formState.registrationFee,
        eventLocation: formState.eventLocation,
        eventLocationLatitude: formState.eventLocationLatitude,
        eventLocationLongitude: formState.eventLocationLongitude,
        eventId: event?.eventId || 0,
        eventOrganizerUserId: formState.eventOrganizerUserId || null,
      };

      const result = await updateEvent(event?.eventId, payload);
      if (result?.isSuccess) {
        if (
          event?.attachmentId &&
          event?.primaryImageUrl &&
          !images.some((i) => !i.isNew)
        ) {
          await deleteAttachmentRequest(event.attachmentId);
        }
        if (images && images.length > 0 && images.some((img) => img.isNew)) {
          const response = await bulkUploadFilesRequest(
            images.map((img) => img.file!),
            EntityType.Event,
            Number(event?.eventId),
            'Event Image',
            getFileOrders(images),
          );
          if (response) {
            if (
              response.value?.failureCount &&
              response.value?.failureCount > 0
            ) {
              snackbar.error(
                `${response.value.failureCount} image(s) failed to upload.`,
              );
            }
          }
        }
        snackbar.success('Event updated successfully!');
        onSuccess();
      } else {
        snackbar.error(
          result?.error ?? 'Failed to update event. Please try again.',
        );
      }
    } catch (error: any) {
      console.error('Update error:', error);
      snackbar.error(
        error?.Error ?? 'Failed to update event. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (changedFields: Record<string, unknown>) => {
    const fieldName = Object.keys(changedFields)[0];
    const fieldValue = changedFields[fieldName];

    setFormState((prev) => ({
      ...prev,
      [fieldName]: fieldValue,
    }));
  };

  const disabledDate = (
    current: Dayjs,
    fieldType: 'regStart' | 'regEnd' | 'eventStart' | 'eventEnd',
  ): boolean => {
    const today = dayjs().startOf('day');

    switch (fieldType) {
      case 'regStart':
        return !!(current && current.isBefore(today));
      case 'regEnd':
        return !!(
          current &&
          (current.isBefore(today) ||
            (formState.registrationStartDate &&
              current.isBefore(formState.registrationStartDate)))
        );
      case 'eventStart':
        return !!(
          current &&
          (current.isBefore(today) ||
            (formState.registrationEndDate &&
              current.isBefore(formState.registrationEndDate)))
        );
      case 'eventEnd':
        return !!(
          current &&
          (current.isBefore(today) ||
            (formState.eventStartDate &&
              current.isBefore(formState.eventStartDate)))
        );
      default:
        return !!(current && current.isBefore(today));
    }
  };

  const beforeUpload = (file: RcFile) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const allowedExtensions = ['jpg', 'jpeg', 'png'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const isValidType =
      allowedTypes.includes(file.type) &&
      fileExtension &&
      allowedExtensions.includes(fileExtension);
    if (!isValidType) {
      snackbar.error('Only jpeg, png and jpg formats allowed!');
      return Upload.LIST_IGNORE;
    }
    return false; // Prevent auto-upload but allow manual processing
  };

  const handleUploadChange: UploadProps['onChange'] = (info) => {
    // Filter out files that failed validation (status 'error' or undefined means beforeUpload returned false)
    const validFiles = info.fileList.filter(
      (f) => f.status !== 'error' && f.originFileObj,
    );

    if (validFiles.length === 0) {
      return; // No valid files to process
    }

    const files = validFiles.map((f) => f.originFileObj as FileType);

    Promise.all(
      files.map(
        (file) =>
          new Promise<string>((resolve) => {
            getBase64(file, (url) => resolve(url));
          }),
      ),
    ).then((urls) => {
      const newImages: UnifiedImage[] = urls.map((url, idx) => ({
        url,
        file: files[idx],
        isPrimary: true,
        isNew: true,
      }));
      setImages((prev) => [...prev.filter((img) => !img.isNew), ...newImages]);
      setFormState((prev) => ({
        ...prev,
        eventImage: urls[0],
      }));
    });
  };

  const removeImage = (index: number) => {
    setFormState((prev) => ({
      ...prev,
      eventImage: null,
    }));
    setImages((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleLocationChange = (location: LocationValue) => {
    const addressString = location.address
      ? `${location.address.street}, ${location.address.city}, ${location.address.state} ${location.address.pincode}`
      : '';
    setFormState((prev) => ({
      ...prev,
      eventLocation: addressString,
      eventLocationLatitude: location.lat,
      eventLocationLongitude: location.lng,
    }));
    form.setFieldValue('eventLocation', addressString);
    setLocationPickerOpen(false);
  };

  if (!event) {
    return null;
  }

  return (
    <FormContainer>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormChange}
        requiredMark={false}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Event Name"
              name="eventName"
              rules={[{ required: true, message: 'Event name is required' }]}
            >
              <Input
                placeholder="Enter event name"
                size="large"
                value={formState.eventName}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    eventName: e.target.value,
                  }))
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Event Description"
              name="eventDescription"
              rules={[{ required: true, message: 'Description is required' }]}
            >
              <TextArea
                placeholder="Enter event description"
                rows={4}
                value={formState.eventDescription}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    eventDescription: e.target.value,
                  }))
                }
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Event Organiser" name="eventOrganizerUserId">
              <Select
                placeholder="Select organiser"
                size="large"
                defaultValue={event.eventOrganizerUserId}
                value={formState.eventOrganizerUserId}
                showSearch
                filterOption={false}
                onSearch={handleOrganiserSearch}
                onChange={(value) =>
                  setFormState((prev) => ({
                    ...prev,
                    eventOrganizerUserId: value,
                  }))
                }
                onPopupScroll={handleOrganiserScroll}
                loading={isLoadingOrganisers}
                notFoundContent={
                  isLoadingOrganisers ? 'Loading...' : 'No organisers found'
                }
                allowClear
              >
                {eventOrganiserOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Registration Start Date"
              name="registrationStartDate"
              rules={[
                {
                  required: true,
                  message: 'Registration start date is required',
                },
              ]}
            >
              <DatePicker
                size="large"
                style={{ width: '100%' }}
                format="DD-MM-YYYY"
                disabledDate={(current) => disabledDate(current, 'regStart')}
                value={formState.registrationStartDate}
                onChange={(date) =>
                  setFormState((prev) => ({
                    ...prev,
                    registrationStartDate: date,
                  }))
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Registration End Date"
              name="registrationEndDate"
              rules={[
                {
                  required: true,
                  message: 'Registration end date is required',
                },
              ]}
            >
              <DatePicker
                size="large"
                style={{ width: '100%' }}
                format="DD-MM-YYYY"
                disabledDate={(current) => disabledDate(current, 'regEnd')}
                value={formState.registrationEndDate}
                onChange={(date) =>
                  setFormState((prev) => ({
                    ...prev,
                    registrationEndDate: date,
                  }))
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Event Start Date"
              name="eventStartDate"
              rules={[
                { required: true, message: 'Event start date is required' },
              ]}
            >
              <DatePicker
                size="large"
                style={{ width: '100%' }}
                format="DD-MM-YYYY"
                disabledDate={(current) => disabledDate(current, 'eventStart')}
                value={formState.eventStartDate}
                onChange={(date) =>
                  setFormState((prev) => ({ ...prev, eventStartDate: date }))
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Event End Date"
              name="eventEndDate"
              rules={[
                { required: true, message: 'Event end date is required' },
              ]}
            >
              <DatePicker
                size="large"
                style={{ width: '100%' }}
                format="DD-MM-YYYY"
                disabledDate={(current) => disabledDate(current, 'eventEnd')}
                value={formState.eventEndDate}
                onChange={(date) =>
                  setFormState((prev) => ({ ...prev, eventEndDate: date }))
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={<span>Upload Image</span>}>
          {images && images.length > 0 ? (
            <Row gutter={16}>
              {images.map((img, index) => (
                <Col key={index}>
                  <ImageContainer>
                    <ImagePreview src={img.url} alt="event" />
                    <DeleteButton
                      type="text"
                      size="small"
                      icon={<DeleteIcon />}
                      onClick={() => {
                        removeImage(index);
                      }}
                    />
                  </ImageContainer>
                </Col>
              ))}
            </Row>
          ) : (
            <Upload
              maxCount={1}
              showUploadList={false}
              beforeUpload={beforeUpload}
              onChange={handleUploadChange}
              accept="image/jpeg,image/png,image/jpg"
            >
              <UploadButton>
                <CameraIcon />
                Upload Image
              </UploadButton>
            </Upload>
          )}
          <SupportedFormatsText>
            Supported formats: JPG, JPEG, PNG
          </SupportedFormatsText>
        </Form.Item>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={
                <LabelWithButton>
                  <span>Location</span>
                  <Slash>/</Slash>
                  <LocationPickerButton
                    type="primary"
                    onClick={() => setLocationPickerOpen(true)}
                    size="small"
                  >
                    <EnvironmentIcon />
                    Pick Location
                  </LocationPickerButton>
                </LabelWithButton>
              }
              name="eventLocation"
              rules={[{ required: true, message: 'Location is required' }]}
            >
              <TextArea
                placeholder="Enter event location or use location picker"
                rows={4}
                value={formState.eventLocation}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    eventLocation: e.target.value,
                  }))
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Event Status"
              name="eventStatus"
              rules={[{ required: true, message: 'Event status is required' }]}
            >
              <Select
                placeholder="Select status"
                size="large"
                value={formState.eventStatus}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, eventStatus: value }))
                }
              >
                {eventStatusOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Registration Fee"
              name="registrationFee"
              rules={[
                { required: true, message: 'Registration fee is required' },
              ]}
            >
              <Input
                type="number"
                placeholder="0"
                size="large"
                min="0"
                step="1"
                value={formState.registrationFee}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const cleaned = e.target.value.replace(/[^0-9]/g, '');
                  setFormState((prev) => ({
                    ...prev,
                    registrationFee: Number(cleaned) || 0,
                  }));
                }}
                onKeyDown={(e) => {
                  if (e.key === '-' || e.key === 'e' || e.key === '+') {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <ButtonContainer>
          <Button size="large" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            loading={loading}
            onClick={handleSubmit}
          >
            Update Event
          </Button>
        </ButtonContainer>
      </Form>

      <Modal
        title="Pick Event Location"
        open={locationPickerOpen}
        onCancel={() => setLocationPickerOpen(false)}
        footer={null}
        width={1200}
      >
        <LocationPicker
          value={
            formState.eventLocationLatitude && formState.eventLocationLongitude
              ? {
                  lat: formState.eventLocationLatitude,
                  lng: formState.eventLocationLongitude,
                  address: undefined,
                }
              : undefined
          }
          onLocationChange={handleLocationChange}
        />
      </Modal>
    </FormContainer>
  );
};

const FormContainer = styled.div`
  padding: 0;

  .ant-form-item-label {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    color: ${COLORS.black.primary};
  }

  .ant-input,
  .ant-input-number,
  .ant-select-selector,
  .ant-picker {
    border-radius: 6px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 24px;
`;

const UploadButton = styled(Button)`
  height: 100px;
  flex-direction: column;
`;

const CameraIcon = styled(CameraOutlined)`
  font-size: ${TEXT_SIZES.LG}px;
`;

const ImageContainer = styled.div`
  position: 'relative';
`;
const ImagePreview = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 8px;
  object-fit: cover;
`;

const DeleteButton = styled(Button)`
  position: absolute;
  top: 4px;
  left: 4px;
  background: ${COLORS.white.primary};
  border-radius: 50%;
`;

const DeleteIcon = styled(DeleteOutlined)`
  color: ${COLORS.red.primary};
`;

const LabelWithButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const LocationPickerButton = styled(Button)`
  display: flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 6px;
  gap: 4px;
`;

const EnvironmentIcon = styled(EnvironmentOutlined)`
  font-size: ${TEXT_SIZES.SM}px;
`;

const Slash = styled.span`
  margin: 0 8px;
  font-size: ${TEXT_SIZES.MD}px;
  color: ${COLORS.grey.primary};
`;

const SupportedFormatsText = styled.p`
  margin: 4px 0px;
  font-size: ${TEXT_SIZES.XS}px;
  color: ${COLORS.grey.secondary};
`;

export default EventEditForm;
